import React, {useEffect, useRef} from 'react';
import {Box} from '@mui/material';
import {SIGN_IN_COLORS} from './sx';
import {CITY_AVENUES, CITY_RAILS, CITY_ROUTES, CITY_STREAMS, CITY_STREETS, CITY_WATER} from './cityMap';

const VIEW_WIDTH = 1600
const VIEW_HEIGHT = 1000
const POINT_STEP = 34
// The trail is the stretch a bus covered over the last part of its round trip: it follows the
// bus instead of growing from the start of the route, so a turn at a terminus never wipes it.
const TRAIL_SHARE = 0.15
// The trail fades from the bus back in bands of these opacities, over the whole route drawn faint.
const TRAIL_BANDS = [1, 0.78, 0.6, 0.45, 0.32]
const ROUTE_OPACITY = 0.22
// Routes are sampled once into points this far apart, so a frame only reads from an array.
const SAMPLE_STEP = 2
const SVG_NS = 'http://www.w3.org/2000/svg'
// The ends of a route may run off the screen; the buses turn back a little before them.
const VISIBLE_FROM = 0.03
const VISIBLE_TO = 0.95
// Where the buses turn back, see headAt.
const TERMINI = [VISIBLE_FROM, VISIBLE_TO]
// A stop lights up while a bus is this close to it along the route.
const STOP_REACH = 30

// Buses on the streets of a real town (see cityMap.ts). The readout follows the first one.
const VEHICLES = [
    {route: CITY_ROUTES[0], loopMs: 72000, offset: 0, stopsAt: [0.14, 0.31, 0.47, 0.63, 0.79]},
    {route: CITY_ROUTES[1], loopMs: 88000, offset: 0.35, stopsAt: [0.2, 0.45, 0.7, 0.9]},
    {route: CITY_ROUTES[2], loopMs: 60000, offset: 0.6, stopsAt: [0.25, 0.55, 0.8]},
]

// The scene's colours, built from the accent for the night or the day map (see sx.ts).
export interface IScenePalette {
    river: string,
    street: string,
    stopFill: string,
    trackPoint: string,
    markerFill: string,
    markerStroke: string,
    // One per bus, in the order of VEHICLES.
    tracks: string[],
    // The stretch a bus drives twice after a turn, one per bus.
    overlaps: string[],
    drone: string,
    droneWing: string,
    contrail: string,
}

// calm: only the buses; defence: drones over the town and the air defence game.
export type SceneMode = 'calm' | 'defence'

// Map frame of the drawing, roughly around Lviv, only to make the readout look like a fix.
const LAT_TOP = 49.921
const LAT_SPAN = 0.205
const LON_LEFT = 23.818
const LON_SPAN = 0.428

const SIGNAL_PERIOD_MS = 1800

export type DroneKind = 'shahed' | 'jet' | 'quad'

interface IFlight {
    kind: DroneKind,
    from: { x: number, y: number },
    to: { x: number, y: number },
    startMs: number,
    durationMs: number,
    // Where air defence takes it down by itself, as a share of the flight; null flies through.
    interceptAt: number | null,
    // Sideways sway of the small drones, in drawing units.
    wobble: number,
}

// Drones crossing the city. Air defence takes some of them down on the way, the visitor can take
// the rest with a click; the bus keeps going either way.
const FLIGHT_CYCLE_MS = 26000
const BURST_MS = 1100
const FLIGHTS: IFlight[] = [
    {kind: 'shahed', from: {x: 1700, y: 140}, to: {x: -120, y: 600}, startMs: 1500, durationMs: 15000, interceptAt: 0.57, wobble: 0},
    {kind: 'shahed', from: {x: 1680, y: 960}, to: {x: 180, y: -140}, startMs: 8000, durationMs: 17000, interceptAt: null, wobble: 0},
    {kind: 'shahed', from: {x: 940, y: -100}, to: {x: -140, y: 820}, startMs: 14000, durationMs: 13000, interceptAt: 0.46, wobble: 0},
    {kind: 'jet', from: {x: 1720, y: 300}, to: {x: -140, y: 60}, startMs: 6000, durationMs: 5200, interceptAt: null, wobble: 0},
    {kind: 'jet', from: {x: 1500, y: -100}, to: {x: 260, y: 1100}, startMs: 19000, durationMs: 5800, interceptAt: 0.62, wobble: 0},
    {kind: 'quad', from: {x: 1660, y: 420}, to: {x: 640, y: -80}, startMs: 3000, durationMs: 8000, interceptAt: null, wobble: 28},
    {kind: 'quad', from: {x: 1250, y: 1080}, to: {x: 1660, y: 150}, startMs: 10000, durationMs: 7000, interceptAt: null, wobble: 22},
    {kind: 'quad', from: {x: 620, y: 1080}, to: {x: 1300, y: -80}, startMs: 17000, durationMs: 9000, interceptAt: null, wobble: 30},
    {kind: 'quad', from: {x: 1680, y: 700}, to: {x: 720, y: 1080}, startMs: 21000, durationMs: 7500, interceptAt: null, wobble: 24},
]

const flightPosition = (flight: IFlight, fraction: number) => {
    const dx = flight.to.x - flight.from.x
    const dy = flight.to.y - flight.from.y
    const distance = Math.hypot(dx, dy)
    const sway = flight.wobble * Math.sin(fraction * Math.PI * 6)
    return {
        x: flight.from.x + dx * fraction - (dy / distance) * sway,
        y: flight.from.y + dy * fraction + (dx / distance) * sway,
    }
}

interface IShot {
    cycle: number,
    at: { x: number, y: number },
    elapsed: number,
}

const ROTORS = [[-7, -7], [7, -7], [-7, 7], [7, 7]]

const SPARKS = [
    {angle: 8, from: 9, to: 22}, {angle: 71, from: 11, to: 17}, {angle: 128, from: 8, to: 25},
    {angle: 197, from: 10, to: 19}, {angle: 244, from: 9, to: 23}, {angle: 311, from: 12, to: 18},
]

const formatFix = (x: number, y: number) => {
    const lat = LAT_TOP - (y / VIEW_HEIGHT) * LAT_SPAN
    const lon = LON_LEFT + (x / VIEW_WIDTH) * LON_SPAN
    return `${lat.toFixed(5)}° N  ${lon.toFixed(5)}° E`
}

const cyclePosition = (elapsed: number, offset: number, cycle: number) => (((elapsed - offset) % cycle) + cycle) % cycle

// Seen from above and pointing along +x: a cropped delta wing with fins on the tips, like the
// Shahed-136. The propeller one pushes from the tail; the jet one carries its engine on top of
// the tail and leaves a contrail.
const DeltaDrone = ({isJet, palette}: { isJet: boolean, palette: IScenePalette }) => (
    <g>
        {isJet && <rect x={-70} y={-1} width={52} height={2} fill={'url(#sign-in-contrail)'}/>}
        <path d={'M 9 -2.2 L -10 -16 L -12 -16 L -12 16 L -10 16 L 9 2.2 Z'} fill={palette.droneWing}/>
        <path d={'M 18 0 Q 16 -2.6 12 -2.6 L -14 -2.2 L -14 2.2 L 12 2.6 Q 16 2.6 18 0 Z'} fill={palette.drone}/>
        <rect x={-13} y={-17} width={8} height={2} rx={1} fill={palette.drone}/>
        <rect x={-13} y={15} width={8} height={2} rx={1} fill={palette.drone}/>
        {isJet ? (
            <>
                <rect x={-16} y={-3.2} width={13} height={6.4} rx={3} fill={palette.drone}/>
                <circle cx={-17} cy={0} r={2.6} fill={SIGN_IN_COLORS.blastCore}/>
            </>
        ) : (
            <>
                <ellipse cx={-15.5} cy={0} rx={1.4} ry={6} fill={palette.drone} opacity={0.35}/>
                <line x1={-15.5} y1={-6} x2={-15.5} y2={6} stroke={palette.drone} strokeWidth={1.2}>
                    <animateTransform
                        attributeName={'transform'}
                        type={'rotate'}
                        values={'0 -15.5 0; 180 -15.5 0'}
                        dur={'0.25s'}
                        repeatCount={'indefinite'}
                    />
                </line>
            </>
        )}
        <circle cx={-12} cy={-16} r={1.2} fill={SIGN_IN_COLORS.blastRing}/>
        <circle cx={-12} cy={16} r={1.2} fill={'#7bd88f'}/>
    </g>
)

interface IProps {
    mode: SceneMode,
    palette: IScenePalette,
    readoutRef: React.RefObject<HTMLSpanElement>,
    onDroneDown?: (kind: DroneKind) => void,
}

interface IRouteSamples {
    length: number,
    xs: Float32Array,
    ys: Float32Array,
}

const sampleRoute = (route: SVGPathElement): IRouteSamples => {
    const length = route.getTotalLength()
    const count = Math.ceil(length / SAMPLE_STEP) + 1
    const xs = new Float32Array(count)
    const ys = new Float32Array(count)
    for (let i = 0; i < count; i++) {
        const {x, y} = route.getPointAtLength(Math.min(length, i * SAMPLE_STEP))
        xs[i] = x
        ys[i] = y
    }
    return {length, xs, ys}
}

const pointOnRoute = ({xs, ys}: IRouteSamples, at: number) => {
    const last = xs.length - 1
    const position = Math.max(0, Math.min(last, at / SAMPLE_STEP))
    const i = Math.min(last - 1, Math.floor(position))
    const f = position - i
    return {
        x: xs[i] + (xs[i + 1] - xs[i]) * f,
        y: ys[i] + (ys[i + 1] - ys[i]) * f,
        angle: Math.atan2(ys[i + 1] - ys[i], xs[i + 1] - xs[i]) * 180 / Math.PI,
    }
}

// A stretch of a route, as distances along it.
interface ISpan {
    from: number,
    to: number,
}

// Shows only the given stretches of a path drawn along the whole route: one dash per stretch.
const drawStretches = (path: SVGPathElement, length: number, spans: ISpan[]) => {
    // A dash of zero length would still leave a dot under a round cap.
    const shown = spans.filter(({from, to}) => to - from > 0.5)
    if (shown.length === 0) {
        path.style.visibility = 'hidden'
        return
    }
    const dashes: string[] = []
    shown.forEach(({from, to}, index) => {
        if (index > 0) {
            dashes.push((from - shown[index - 1].to).toFixed(1))
        }
        dashes.push((to - from).toFixed(1))
    })
    dashes.push(length.toFixed(0))
    path.style.strokeDasharray = dashes.join(' ')
    path.style.strokeDashoffset = (-shown[0].from).toFixed(1)
    path.style.visibility = 'visible'
}

// Two stacked drawings with the same frame: the map never changes once drawn, so the browser
// keeps it as is, and only the layer with the buses and the drones repaints on every frame.
const layerSx = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    willChange: 'transform',
} as const

const TrackScene = ({mode, palette, readoutRef, onDroneDown}: IProps) => {

    const mapRef = useRef<SVGSVGElement>(null)
    const routeRefs = useRef<(SVGPathElement | null)[]>([])
    const bandRefs = useRef<(SVGPathElement | null)[][]>(VEHICLES.map(() => []))
    const haloRefs = useRef<(SVGPathElement | null)[]>([])
    const overlapRefs = useRef<(SVGPathElement | null)[]>([])
    const markerRefs = useRef<(SVGGElement | null)[]>([])
    const pointsRefs = useRef<(SVGGElement | null)[]>([])
    const stopsRefs = useRef<(SVGGElement | null)[]>([])
    const terminiRefs = useRef<(SVGGElement | null)[]>([])
    const signalRefs = useRef<(SVGCircleElement | null)[][]>(VEHICLES.map(() => []))
    const droneRefs = useRef<(SVGGElement | null)[]>([])
    const burstRefs = useRef<(SVGGElement | null)[]>([])
    // Shared between the animation loop and the click handler.
    const elapsedRef = useRef(0)
    const positionsRef = useRef<{ x: number, y: number }[]>(FLIGHTS.map((flight) => flight.from))
    const shotsRef = useRef<(IShot | null)[]>(FLIGHTS.map(() => null))
    const onDroneDownRef = useRef(onDroneDown)
    onDroneDownRef.current = onDroneDown

    const cycleOf = (flight: IFlight, elapsed: number) => Math.floor((elapsed - flight.startMs) / FLIGHT_CYCLE_MS)

    const shootDown = (index: number) => {
        const flight = FLIGHTS[index]
        const elapsed = elapsedRef.current
        const shot = shotsRef.current[index]
        if (shot && shot.cycle === cycleOf(flight, elapsed)) {
            return
        }
        shotsRef.current[index] = {cycle: cycleOf(flight, elapsed), at: positionsRef.current[index], elapsed}
        onDroneDownRef.current?.(flight.kind)
    }

    useEffect(() => {
        const buses = VEHICLES.map((vehicle, index) => {
            const route = routeRefs.current[index]
            const bands = bandRefs.current[index].filter((band): band is SVGPathElement => band !== null)
            const halo = haloRefs.current[index]
            const overlap = overlapRefs.current[index]
            const marker = markerRefs.current[index]
            const pointsGroup = pointsRefs.current[index]
            const stopsGroup = stopsRefs.current[index]
            const terminiGroup = terminiRefs.current[index]
            if (!route || bands.length !== TRAIL_BANDS.length || !halo || !overlap || !marker || !pointsGroup || !stopsGroup || !terminiGroup) {
                return null
            }
            const samples = sampleRoute(route)
            const {length} = samples

            const stops = vehicle.stopsAt.map((fraction) => {
                const at = length * fraction
                const {x, y} = pointOnRoute(samples, at)
                const stop = document.createElementNS(SVG_NS, 'circle')
                stop.setAttribute('cx', x.toFixed(1))
                stop.setAttribute('cy', y.toFixed(1))
                stop.setAttribute('r', '3.5')
                stopsGroup.appendChild(stop)
                return {at, element: stop, isLit: false}
            })

            TERMINI.forEach((fraction) => {
                const {x, y} = pointOnRoute(samples, length * fraction)
                const terminus = document.createElementNS(SVG_NS, 'g')
                terminus.setAttribute('transform', `translate(${x.toFixed(1)} ${y.toFixed(1)})`)
                const outer = document.createElementNS(SVG_NS, 'rect')
                outer.setAttribute('x', '-8')
                outer.setAttribute('y', '-8')
                outer.setAttribute('width', '16')
                outer.setAttribute('height', '16')
                outer.setAttribute('rx', '4')
                const inner = document.createElementNS(SVG_NS, 'rect')
                inner.setAttribute('x', '-3')
                inner.setAttribute('y', '-3')
                inner.setAttribute('width', '6')
                inner.setAttribute('height', '6')
                inner.setAttribute('rx', '1')
                inner.setAttribute('class', 'terminus-core')
                terminus.append(outer, inner)
                terminiGroup.appendChild(terminus)
            })

            const points: SVGCircleElement[] = []
            for (let at = POINT_STEP; at < length; at += POINT_STEP) {
                const {x, y} = pointOnRoute(samples, at)
                const circle = document.createElementNS(SVG_NS, 'circle')
                circle.setAttribute('cx', x.toFixed(1))
                circle.setAttribute('cy', y.toFixed(1))
                circle.setAttribute('r', '2.4')
                // Hidden until the bus passes, so a rebuilt scene doesn't flash every point.
                circle.style.opacity = '0'
                circle.style.transition = 'opacity 400ms'
                pointsGroup.appendChild(circle)
                points.push(circle)
            }
            // lit: the points under the trail, so a frame only touches the ones that came in or out.
            return {
                vehicle, index, bands, halo, overlap, marker, pointsGroup, stopsGroup, terminiGroup, samples, points, stops,
                lit: {from: 0, to: 0},
            }
        }).filter((bus): bus is NonNullable<typeof bus> => bus !== null)

        // A bus goes to the far terminus and back, easing into each turn, so the motion never jumps.
        const phaseAt = (bus: typeof buses[number], elapsed: number) =>
            ((elapsed / bus.vehicle.loopMs + bus.vehicle.offset) % 1 + 1) % 1
        const headAt = (bus: typeof buses[number], phase: number) =>
            bus.samples.length * (VISIBLE_FROM + (1 - Math.cos(phase * Math.PI * 2)) / 2 * (VISIBLE_TO - VISIBLE_FROM))

        // The stretch of the route a bus covered between two phases (p1 <= p2, either may be
        // negative), including a terminus if it turned there meanwhile.
        const spanOf = (bus: typeof buses[number], p1: number, p2: number): ISpan => {
            const a = headAt(bus, (p1 % 1 + 1) % 1)
            const b = headAt(bus, (p2 % 1 + 1) % 1)
            return {
                from: Math.floor(p2) >= Math.ceil(p1) ? headAt(bus, 0) : Math.min(a, b),
                to: Math.floor(p2 - 0.5) >= Math.ceil(p1 - 0.5) ? headAt(bus, 0.5) : Math.max(a, b),
            }
        }

        const drawBus = (bus: typeof buses[number], elapsed: number) => {
            const {bands, halo, overlap, marker, samples, points, stops, lit} = bus
            const phase = phaseAt(bus, elapsed)
            const head = headAt(bus, phase)

            // The trail fades in bands from the bus back. Every stretch goes to the band of the
            // last time the bus drove it, so after a turn the bands don't pile up on each other.
            const bandShare = TRAIL_SHARE / bands.length
            const trail = {from: head, to: head}
            bands.forEach((band, index) => {
                const span = spanOf(bus, phase - (index + 1) * bandShare, phase - index * bandShare)
                drawStretches(band, samples.length, [
                    {from: span.from, to: Math.min(span.to, trail.from)},
                    {from: Math.max(span.from, trail.to), to: span.to},
                ])
                trail.from = Math.min(trail.from, span.from)
                trail.to = Math.max(trail.to, span.to)
            })
            drawStretches(halo, samples.length, [trail])

            // After a turn the bus drives back over its own trail: that stretch is covered twice.
            const pastPhase = phase - TRAIL_SHARE
            const past = headAt(bus, (pastPhase + 1) % 1)
            let doubled = {from: 0, to: 0}
            if (pastPhase < 0.5 && phase >= 0.5) {
                doubled = {from: Math.max(head, past), to: trail.to}
            }
            if (pastPhase < 0) {
                doubled = {from: trail.from, to: Math.min(head, past)}
            }
            drawStretches(overlap, samples.length, [doubled])
            const trailFrom = trail.from
            const trailTo = trail.to

            const {x, y, angle} = pointOnRoute(samples, head)
            const heading = phase < 0.5 ? angle : angle + 180
            marker.setAttribute('transform', `translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${heading.toFixed(1)})`)

            // Point i sits at (i + 1) * POINT_STEP along the route.
            const from = Math.max(0, Math.ceil(trailFrom / POINT_STEP) - 1)
            const to = Math.min(points.length, Math.floor(trailTo / POINT_STEP))
            for (let i = lit.from; i < lit.to; i++) {
                if (i < from || i >= to) {
                    points[i].style.opacity = '0'
                }
            }
            for (let i = from; i < to; i++) {
                if (i < lit.from || i >= lit.to) {
                    points[i].style.opacity = '1'
                }
            }
            bus.lit = {from, to}

            // Only a change of state touches the DOM; the CSS transition does the rest.
            stops.forEach((stop) => {
                const isLit = Math.abs(stop.at - head) < STOP_REACH
                if (isLit !== stop.isLit) {
                    stop.element.classList.toggle('stop-lit', isLit)
                    stop.isLit = isLit
                }
            })
        }

        const drawSignal = (bus: typeof buses[number], elapsed: number) => {
            const rings = signalRefs.current[bus.index]
            rings.forEach((ring, index) => {
                if (!ring) {
                    return
                }
                const phase = (elapsed / SIGNAL_PERIOD_MS + index / rings.length + bus.vehicle.offset) % 1
                ring.setAttribute('r', (12 + phase * 46).toFixed(1))
                ring.style.opacity = ((1 - phase) * 0.7).toFixed(2)
            })
        }

        const drawFlights = (elapsed: number) => {
            FLIGHTS.forEach((flight, index) => {
                const drone = droneRefs.current[index]
                const burst = burstRefs.current[index]
                if (!drone || !burst) {
                    return
                }
                const t = cyclePosition(elapsed, flight.startMs, FLIGHT_CYCLE_MS) / flight.durationMs
                const downAt = flight.interceptAt ?? 1
                const shot = shotsRef.current[index]
                const isShotThisCycle = shot !== null && shot.cycle === cycleOf(flight, elapsed)

                if (t < downAt && !isShotThisCycle) {
                    const {x, y} = flightPosition(flight, t)
                    positionsRef.current[index] = {x, y}
                    const heading = Math.atan2(flight.to.y - flight.from.y, flight.to.x - flight.from.x) * 180 / Math.PI
                    drone.setAttribute('transform', `translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${heading.toFixed(1)})`)
                    drone.style.opacity = '1'
                    drone.style.pointerEvents = 'auto'
                } else {
                    drone.style.opacity = '0'
                    drone.style.pointerEvents = 'none'
                }

                let burstProgress = 1
                let burstAt = flightPosition(flight, downAt)
                if (shot !== null && elapsed - shot.elapsed < BURST_MS) {
                    burstProgress = (elapsed - shot.elapsed) / BURST_MS
                    burstAt = shot.at
                } else if (flight.interceptAt !== null && !isShotThisCycle) {
                    burstProgress = (t - downAt) * flight.durationMs / BURST_MS
                }
                if (burstProgress >= 0 && burstProgress < 1) {
                    const {x, y} = burstAt
                    const scale = (0.4 + burstProgress * 1.6) * (flight.kind === 'quad' ? 0.6 : 1)
                    burst.setAttribute('transform', `translate(${x.toFixed(1)} ${y.toFixed(1)}) scale(${scale.toFixed(2)})`)
                    burst.style.opacity = (1 - burstProgress).toFixed(2)
                } else {
                    burst.style.opacity = '0'
                }
            })
        }

        const cleanUp = () => {
            buses.forEach(({pointsGroup, stopsGroup, terminiGroup}) => {
                pointsGroup.replaceChildren()
                stopsGroup.replaceChildren()
                terminiGroup.replaceChildren()
            })
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            buses.forEach((bus) => drawBus(bus, 0.3 * bus.vehicle.loopMs))
            return cleanUp
        }

        let frame = 0
        const started = performance.now()
        const tick = (now: number) => {
            const elapsed = now - started
            elapsedRef.current = elapsed
            buses.forEach((bus) => {
                drawBus(bus, elapsed)
                drawSignal(bus, elapsed)
            })
            if (mode === 'defence') {
                drawFlights(elapsed)
            }
            frame = requestAnimationFrame(tick)
        }
        frame = requestAnimationFrame(tick)

        return () => {
            cancelAnimationFrame(frame)
            cleanUp()
        }
    }, [mode]);

    // The readout shows the map coordinates under the mouse, the centre until it moves.
    useEffect(() => {
        const svg = mapRef.current
        const readout = readoutRef.current
        if (!svg || !readout) {
            return
        }
        readout.textContent = formatFix(VIEW_WIDTH / 2, VIEW_HEIGHT / 2)
        let frame = 0
        const onMove = (event: PointerEvent) => {
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
                const toMap = svg.getScreenCTM()?.inverse()
                if (!toMap) {
                    return
                }
                const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(toMap)
                readout.textContent = formatFix(point.x, point.y)
            })
        }
        window.addEventListener('pointermove', onMove)
        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('pointermove', onMove)
        }
    }, [readoutRef]);

    const viewBox = `0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`

    return (
        <>
            <Box
                ref={mapRef}
                component={'svg'}
                viewBox={viewBox}
                preserveAspectRatio={'xMidYMid slice'}
                aria-hidden={true}
                sx={layerSx}
            >
                <path d={CITY_WATER} fill={palette.river}/>
                <path d={CITY_STREAMS} fill={'none'} stroke={palette.river} strokeWidth={4} strokeLinecap={'round'}/>

                <g fill={'none'} stroke={palette.street} strokeLinecap={'round'} strokeLinejoin={'round'}>
                    <path d={CITY_STREETS} strokeWidth={1.2} opacity={0.6}/>
                    <path d={CITY_AVENUES} strokeWidth={3} opacity={0.95}/>
                    <path d={CITY_RAILS} strokeWidth={1.2} strokeDasharray={'6 5'} opacity={0.7}/>
                </g>

                {/* The whole route, faint, under the trail. The group is faint, not the strokes, so
                    where routes share a street they don't add up. */}
                <g opacity={ROUTE_OPACITY}>
                    {VEHICLES.map((vehicle, index) => (
                        <path
                            key={index}
                            ref={(element) => {
                                routeRefs.current[index] = element
                            }}
                            d={vehicle.route}
                            fill={'none'}
                            stroke={palette.tracks[index]}
                            strokeWidth={3}
                            strokeLinecap={'round'}
                            strokeLinejoin={'round'}
                        />
                    ))}
                </g>

                {VEHICLES.map((vehicle, index) => (
                    <g key={index}>
                        {/* Termini: a square in the colour of the route, unlike the round stops. */}
                        <Box
                            component={'g'}
                            ref={(element: SVGGElement | null) => {
                                terminiRefs.current[index] = element
                            }}
                            sx={{
                                fill: palette.tracks[index],
                                stroke: palette.stopFill,
                                strokeWidth: 2,
                                '& .terminus-core': {fill: palette.stopFill, stroke: 'none'},
                            }}
                        />
                    </g>
                ))}
            </Box>

            <Box
                component={'svg'}
                viewBox={viewBox}
                preserveAspectRatio={'xMidYMid slice'}
                aria-hidden={true}
                sx={layerSx}
            >
                <defs>
                    <linearGradient id={'sign-in-contrail'} x1={'0'} x2={'1'} y1={'0'} y2={'0'}>
                        <stop offset={'0%'} stopColor={palette.contrail} stopOpacity={0}/>
                        <stop offset={'100%'} stopColor={palette.contrail} stopOpacity={0.45}/>
                    </linearGradient>
                    <radialGradient id={'sign-in-blast'}>
                        <stop offset={'0%'} stopColor={'#fff4d6'}/>
                        <stop offset={'35%'} stopColor={SIGN_IN_COLORS.blastCore}/>
                        <stop offset={'70%'} stopColor={SIGN_IN_COLORS.blastRing} stopOpacity={0.55}/>
                        <stop offset={'100%'} stopColor={SIGN_IN_COLORS.blastRing} stopOpacity={0}/>
                    </radialGradient>
                </defs>

                {/* A wide faint stroke under each track gives the glow a blur filter would, without
                    redrawing a filter on every frame. The group is faint, not the strokes, so where
                    the tracks cross the glows don't pile up into a darker blot. */}
                <g opacity={0.22}>
                    {VEHICLES.map((vehicle, index) => (
                        <path
                            key={index}
                            ref={(element) => {
                                haloRefs.current[index] = element
                            }}
                            d={vehicle.route}
                            fill={'none'}
                            stroke={palette.tracks[index]}
                            strokeWidth={10}
                            strokeLinecap={'round'}
                            strokeLinejoin={'round'}
                            visibility={'hidden'}
                        />
                    ))}
                </g>

                {VEHICLES.map((vehicle, index) => (
                    <g key={index}>
                        {TRAIL_BANDS.map((opacity, band) => (
                            <path
                                key={band}
                                ref={(element) => {
                                    bandRefs.current[index][band] = element
                                }}
                                d={vehicle.route}
                                fill={'none'}
                                stroke={palette.tracks[index]}
                                strokeOpacity={opacity}
                                strokeWidth={3}
                                strokeLinejoin={'round'}
                                visibility={'hidden'}
                            />
                        ))}
                        {/* The stretch driven twice: a thin line down the middle, like a two-way track. */}
                        <path
                            ref={(element) => {
                                overlapRefs.current[index] = element
                            }}
                            d={vehicle.route}
                            fill={'none'}
                            stroke={palette.overlaps[index]}
                            strokeWidth={1.2}
                            strokeLinejoin={'round'}
                            visibility={'hidden'}
                        />
                        <g
                            ref={(element) => {
                                pointsRefs.current[index] = element
                            }}
                            fill={palette.trackPoint}
                        />
                        {/* Small dots that swell and fill with the route colour while a bus is at them. */}
                        <Box
                            component={'g'}
                            ref={(element: SVGGElement | null) => {
                                stopsRefs.current[index] = element
                            }}
                            sx={{
                                fill: palette.stopFill,
                                stroke: palette.tracks[index],
                                strokeWidth: 1.5,
                                '& circle': {
                                    transformBox: 'fill-box',
                                    transformOrigin: 'center',
                                    transition: 'transform 350ms ease-out, fill 350ms ease-out',
                                },
                                '& .stop-lit': {
                                    fill: palette.tracks[index],
                                    transform: 'scale(2.2)',
                                },
                                '@media (prefers-reduced-motion: reduce)': {
                                    '& circle': {transition: 'none'},
                                },
                            }}
                        />
                        <g
                            ref={(element) => {
                                markerRefs.current[index] = element
                            }}
                        >
                            {[0, 1, 2].map((ring) => (
                                <circle
                                    key={ring}
                                    ref={(element) => {
                                        signalRefs.current[index][ring] = element
                                    }}
                                    r={12}
                                    fill={'none'}
                                    stroke={palette.tracks[index]}
                                    strokeWidth={1.5}
                                    opacity={0}
                                />
                            ))}
                            <circle r={16} fill={palette.tracks[index]} opacity={0.18}/>
                            <circle r={9} fill={palette.markerFill} stroke={palette.markerStroke} strokeWidth={2}/>
                            <path d={'M -3 -4 L 4 0 L -3 4 Z'} fill={palette.markerStroke}/>
                        </g>
                    </g>
                ))}

                {mode === 'defence' && FLIGHTS.map((flight, index) => (
                    <React.Fragment key={index}>
                        <g
                            ref={(element) => {
                                droneRefs.current[index] = element
                            }}
                            opacity={0}
                            onPointerDown={() => shootDown(index)}
                            style={{cursor: 'crosshair', pointerEvents: 'none'}}
                        >
                            {/* A larger invisible target than the silhouette, so a click can land. */}
                            <circle r={flight.kind === 'quad' ? 24 : 30} fill={'transparent'}/>
                            {flight.kind !== 'quad' ? (
                                <DeltaDrone isJet={flight.kind === 'jet'} palette={palette}/>
                            ) : (
                                <g stroke={palette.drone} fill={'none'} strokeWidth={1.6}>
                                    {/* A quadcopter from above: X frame, four rotors, body. */}
                                    <line x1={-7} y1={-7} x2={7} y2={7}/>
                                    <line x1={-7} y1={7} x2={7} y2={-7}/>
                                    {ROTORS.map(([cx, cy]) => (
                                        <circle key={`${cx}:${cy}`} cx={cx} cy={cy} r={3.6}/>
                                    ))}
                                    <circle r={2.4} fill={palette.drone}/>
                                </g>
                            )}
                        </g>
                        <g
                            ref={(element) => {
                                burstRefs.current[index] = element
                            }}
                            opacity={0}
                        >
                            {/* Shock wave, flash and a few uneven sparks: an interception, not a target mark. */}
                            <circle r={30} fill={'none'} stroke={SIGN_IN_COLORS.blastRing} strokeWidth={1} opacity={0.5}/>
                            <circle r={18} fill={'url(#sign-in-blast)'}/>
                            <g stroke={SIGN_IN_COLORS.blastCore} strokeWidth={1.2} strokeLinecap={'round'}>
                                {SPARKS.map(({angle, from, to}) => (
                                    <line key={angle} x1={from} y1={0} x2={to} y2={0} transform={`rotate(${angle})`}/>
                                ))}
                            </g>
                        </g>
                    </React.Fragment>
                ))}
            </Box>
        </>
    );
};

export default TrackScene;
